import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import './RegistrationPage.css';

const RECAPTCHA_SITE_KEY = '6Ld0ImMsAAAAAHqPz5q3NlfJhlRVjBrez7cxKuPV';

function RegistrationPage() {
  const [user, setUser] = useState({
    firstname: '',
    lastname: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showEula, setShowEula] = useState(false); // State to show/hide EULA
  const [eulaAccepted, setEulaAccepted] = useState(false); // State to track EULA acceptance
  const [captchaToken, setCaptchaToken] = useState(null);
  const recaptchaRef = useRef(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const handleChange = (e) => {
    setUser({
      ...user,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!eulaAccepted) {
      setError('You must accept the EULA to register.');
      return;
    }

    if (!captchaToken) {
      setError('Please complete the CAPTCHA verification.');
      return;
    }

    if (user.password !== user.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    // Password validation
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/;
    if (!passwordRegex.test(user.password)) {
      setError('Password must be at least 8 characters and include uppercase, lowercase, and a special character.');
      return;
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/users/register`, {
        firstname: user.firstname,
        lastname: user.lastname,
        username: user.username,
        email: user.email,
        password: user.password,
        captchaToken: captchaToken,
      });

      console.log('Registration successful:', response);
      const redirectParam = searchParams.get('redirect');
      if (redirectParam) {
        navigate(`/login?redirect=${encodeURIComponent(redirectParam)}&registered=true`);
      } else {
        navigate('/login?registered=true');
      }
    } catch (error) {
      console.error('Registration error:', error.response ? error.response.data : error);
      if (error.response) {
        setError(error.response.data.message);
      } else {
        setError('Registration failed. Please check your details and try again.');
      }
      // Reset captcha on error
      setCaptchaToken(null);
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleEulaClose = () => {
    setShowEula(false);
  };

  const handleEulaAccept = () => {
    setEulaAccepted(true);
    setShowEula(false);
  };

  return (
    <div className="registration-container">
      <div className="registration-logo">
        <span className="logo-air">Air</span><span className="logo-flyp">Flyp</span>
      </div>
      <h1>Create your account</h1>
      <p className="registration-subtitle">Join AirFlyp — your AI-powered workspace</p>
      {error && <p className="error-message">{error}</p>}
      <form onSubmit={handleSubmit} className="registration-form">
        <div className="form-group">
          <label>First Name:</label>
          <input
            type="text"
            name="firstname"
            value={user.firstname}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Last Name:</label>
          <input
            type="text"
            name="lastname"
            value={user.lastname}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Username:</label>
          <input
            type="text"
            name="username"
            value={user.username}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Email:</label>
          <input
            type="email"
            name="email"
            value={user.email}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group">
          <label>Password:</label>
          <div className="password-input-wrapper">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={user.password}
              onChange={handleChange}
              required
            />
            <button
              type="button"
              className="toggle-password-btn"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
          <p className="password-requirements">
            Password must be at least 8 characters and include uppercase, lowercase, and a special character.
          </p>
        </div>
        <div className="form-group">
          <label>Confirm Password:</label>
          <div className="password-input-wrapper">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={user.confirmPassword}
              onChange={handleChange}
              required
            />
            <button
              type="button"
              className="toggle-password-btn"
              onClick={toggleConfirmPasswordVisibility}
            >
              {showConfirmPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

{/* EULA Checkbox */}
<div className="form-group eula-group">
  <input
    type="checkbox"
    id="eulaCheckbox"
    checked={eulaAccepted}
    onChange={() => setEulaAccepted(!eulaAccepted)}
  />
  <label htmlFor="eulaCheckbox">
    I have read and accept the{' '}
    <button type="button" className="eula-link" onClick={() => setShowEula(true)}>
      End User License Agreement (EULA)
    </button>
  </label>
</div>

        <div className="captcha-group">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={(token) => setCaptchaToken(token)}
            onExpired={() => setCaptchaToken(null)}
          />
        </div>

        <button type="submit" className="register-btn" disabled={!eulaAccepted || !captchaToken}>
          Register
        </button>
      </form>

{/* EULA Modal */}
{showEula && (
  <div className="eula-modal">
    <div className="eula-content">
      <h2>End User License Agreement (EULA)</h2>
      <div className="eula-text">
        <p><strong>Effective Date</strong>: September 1, 2024</p>
        <p><strong>Company</strong>: Technolica, Inc.</p>
        <p><strong>Application</strong>: AirFlyp</p>

        <p><strong>PLEASE READ THIS AGREEMENT CAREFULLY BEFORE USING THE SOFTWARE.</strong></p>
        <p>
          This End User License Agreement ("Agreement") is a legal agreement between you ("User") and
          Technolica, Inc. ("Company") concerning your use of the AirFlyp software ("Software"),
          provided as a software-as-a-service (SaaS) platform with integrated AI components.
        </p>
        <p>
          By clicking "Accept," accessing, or otherwise using the Software, you agree to be bound by
          the terms of this Agreement.
        </p>

        <h3>1. Grant of License</h3>
        <p>
          Technolica grants you a limited, non-exclusive, non-transferable, and revocable license to
          access and use the Software solely for your personal or professional purposes. This license
          is subject to compliance with all terms outlined in this Agreement.
        </p>

        <h3>2. Use of the Software</h3>
        <p>AirFlyp is a productivity and team collaboration platform for professionals managing clients,
          schedules, and team workspaces. Users may not:</p>
        <ul>
          <li>Reverse engineer, decompile, or attempt to derive the source code of the Software.</li>
          <li>Use the Software to develop competing products or services.</li>
          <li>Share or disclose access credentials to unauthorized persons.</li>
        </ul>

        <h3>3. AI Components and Data Processing</h3>
        <p>AirFlyp integrates AI components for features including transcription, session summaries,
          AI Persona, and analytics. By using these features, you acknowledge that:</p>
        <ul>
          <li><strong>Data Processing</strong>: Data you provide may be processed by AI models to deliver
            insights and recommendations.</li>
          <li><strong>Accuracy Disclaimer</strong>: AI-generated outputs may not be 100% accurate.
            Exercise your own professional judgment when acting on AI suggestions.</li>
        </ul>

        <h3>4. Privacy and Data Security</h3>
        <p>Technolica takes reasonable measures to protect your personal data. Your data will be handled
          in accordance with applicable data protection laws. For more information, contact us at
          support@airflyp.com.</p>

        <h3>5. User Responsibilities</h3>
        <p>You agree to provide accurate information, maintain the security of your account, and comply
          with all applicable laws in connection with your use of the Software.</p>

        <h3>6. Intellectual Property</h3>
        <p>All rights in and to the Software, including graphics, logos, algorithms, and underlying
          technology, remain the exclusive property of Technolica, Inc. No ownership rights are
          granted under this Agreement.</p>

        <h3>7. Subscription and Fees</h3>
        <p>Access to certain features requires a paid subscription. Failure to maintain payment may
          result in suspension or downgrade of your account.</p>

        <h3>8. Termination</h3>
        <p>Technolica may terminate your access for any breach of this Agreement. Upon termination,
          you must cease all use of the Software.</p>

        <h3>9. Disclaimer of Warranties</h3>
        <p>THE SOFTWARE IS PROVIDED "AS IS" WITHOUT WARRANTY OF ANY KIND. TECHNOLICA DOES NOT WARRANT
          THAT THE SOFTWARE WILL BE ERROR-FREE OR UNINTERRUPTED.</p>

        <h3>10. Limitation of Liability</h3>
        <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, TECHNOLICA SHALL NOT BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE SOFTWARE.</p>

        <h3>11. Amendments</h3>
        <p>Technolica reserves the right to modify this Agreement. Continued use after modifications
          indicates acceptance of the new terms.</p>

        <h3>12. Contact</h3>
        <p>Questions about this Agreement? Contact us at:</p>
        <p>Technolica, Inc. · 4096 Piedmont Ave #985, Oakland, CA 94611</p>
        <p>support@airflyp.com · (619) 292-0358</p>

        <p><strong>By clicking "Accept," you acknowledge that you have read and agree to this Agreement.</strong></p>
      </div>

      <div className="eula-buttons">
        <button className="eula-btn accept-btn" onClick={handleEulaAccept}>
          Accept
        </button>
        <button className="eula-btn close-btn" onClick={handleEulaClose}>
          Close
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
}

export default RegistrationPage;